import type { Page } from '@/requests/api';
import { SpecialPage } from '@/stores/api';
import type { SelectedPage } from '@/stores/api';
import type { LocationQuery, LocationQueryRaw } from 'vue-router';

export const VIEWER_ROUTE_QUERY_KEYS = {
  page: 'page',
  search: 'search',
  index: 'index',
  hash: 'hash',
} as const;

export type ViewerRouteState = {
  page: string | null;
  search: string;
  index: number | null;
  hash: string | null;
};

type ViewerRouteStateInput = {
  page?: string | null;
  search?: string | null;
  index?: number | null;
  hash?: string | null;
};

function getSingleQueryValue(
  value: LocationQuery[string] | LocationQueryRaw[string] | undefined,
): string | null {
  if (Array.isArray(value)) {
    return value.length > 0 ? getSingleQueryValue(value[0]) : null;
  }
  if (value === null || value === undefined) {
    return null;
  }
  const normalized = `${value}`.trim();
  return normalized === '' ? null : normalized;
}

function normalizeRouteIndex(rawIndex: string | null): number | null {
  if (rawIndex === null) {
    return null;
  }
  const parsed = Number.parseInt(rawIndex, 10);
  if (!Number.isFinite(parsed) || Number.isNaN(parsed)) {
    return null;
  }
  return Math.max(0, parsed);
}

export function readViewerRouteState(query: LocationQuery): ViewerRouteState {
  return {
    page: getSingleQueryValue(query[VIEWER_ROUTE_QUERY_KEYS.page]),
    search: getSingleQueryValue(query[VIEWER_ROUTE_QUERY_KEYS.search]) ?? '',
    index: normalizeRouteIndex(getSingleQueryValue(query[VIEWER_ROUTE_QUERY_KEYS.index])),
    hash: getSingleQueryValue(query[VIEWER_ROUTE_QUERY_KEYS.hash]),
  };
}

export function hasViewerRouteState(query: LocationQuery): boolean {
  const state = readViewerRouteState(query);
  return state.page !== null || state.search !== '' || state.index !== null || state.hash !== null;
}

export function buildViewerRouteQuery(
  baseQuery: LocationQuery | LocationQueryRaw,
  state: ViewerRouteStateInput,
): LocationQueryRaw {
  const nextQuery: LocationQueryRaw = { ...baseQuery };

  if (state.page === undefined) {
    // keep existing value untouched
  } else if (state.page === null || state.page === '') {
    delete nextQuery[VIEWER_ROUTE_QUERY_KEYS.page];
  } else {
    nextQuery[VIEWER_ROUTE_QUERY_KEYS.page] = state.page;
  }

  if (state.search === undefined) {
    // keep existing value untouched
  } else if (state.search === null || state.search.trim() === '') {
    delete nextQuery[VIEWER_ROUTE_QUERY_KEYS.search];
  } else {
    nextQuery[VIEWER_ROUTE_QUERY_KEYS.search] = state.search;
  }

  if (state.index === undefined) {
    // keep existing value untouched
  } else if (state.index === null) {
    delete nextQuery[VIEWER_ROUTE_QUERY_KEYS.index];
  } else {
    nextQuery[VIEWER_ROUTE_QUERY_KEYS.index] = `${Math.max(0, Math.floor(state.index))}`;
  }

  if (state.hash === undefined) {
    // keep existing value untouched
  } else if (state.hash === null || state.hash === '') {
    delete nextQuery[VIEWER_ROUTE_QUERY_KEYS.hash];
  } else {
    nextQuery[VIEWER_ROUTE_QUERY_KEYS.hash] = state.hash;
  }

  return nextQuery;
}

export function getViewerRouteQueryKey(query: LocationQuery | LocationQueryRaw): string {
  const parts: string[] = [];

  Object.keys(query)
    .sort((left, right) => left.localeCompare(right))
    .forEach((key) => {
      const rawValue = query[key];
      let values: (string | number | null)[] = [];
      if (Array.isArray(rawValue)) {
        values = rawValue;
      } else if (rawValue !== null && rawValue !== undefined) {
        values = [rawValue];
      }

      values.forEach((value) => {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(`${value}`)}`);
      });
    });

  return parts.join('&');
}

export function serializeSelectedPageForRoute(selectedPage: Page | SpecialPage | null): string | null {
  if (selectedPage === null) {
    return null;
  }
  if (selectedPage === SpecialPage.SEARCH) {
    return 'search';
  }
  if (selectedPage === SpecialPage.CURRENT) {
    return 'current';
  }
  return selectedPage.page_key;
}

function getPageByKey(key: Page['page_key'], page: Page): Page | null {
  if (page.page_key === key) {
    return page;
  }

  for (const nestedPage of page.pages || []) {
    const foundPage = getPageByKey(key, nestedPage);
    if (foundPage !== null) {
      return foundPage;
    }
  }

  return null;
}

export function resolveSelectedPageFromRouteToken(
  pageToken: string | null,
  pages: Page | null,
): SelectedPage | undefined {
  if (pageToken === null) {
    return undefined;
  }
  if (pageToken === 'search') {
    return SpecialPage.SEARCH;
  }
  if (pageToken === 'current') {
    return SpecialPage.CURRENT;
  }
  if (pages === null) {
    return undefined;
  }

  return getPageByKey(pageToken, pages) ?? undefined;
}
